import type {CpcBuyFilters, CpcBuyStep} from './types';

import {Button} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useURLFilter} from 'hooks/useURLFilter';
import {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {confirmCPCOTP, preorderPlanCreate, preorderPlanGet} from 'store/slices/cpc/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import BuyProgress from './components/BuyProgress/BuyProgress';
import ClickRateStep from './components/ClickRateStep/ClickRateStep';
import FinalCheckStep from './components/FinalCheckStep/FinalCheckStep';
import RangePickerStep from './components/RangePickerStep/RangePickerStep';

const OTP_COUNTDOWN_SECONDS = 120;
const CPC_BUY_STEPS: CpcBuyStep[] = [
  {id: 1, key: 'range-picker', title: ''},
  {id: 2, key: 'click-rate', title: ''},
  {id: 3, key: 'final-check', title: ''}
];

const CpcBuy: React.FC = () => {
  const {step: stepParam} = useParams();
  const {search} = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';
  const {preorderCPC, confirmPaymentLoading, vendorNumber} = useAppSelector((state) => state.cpc);
  const defaultFilters = useMemo<CpcBuyFilters>(() => ({}), []);
  const [filters, setFilters] = useURLFilter<CpcBuyFilters>('data', defaultFilters);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [otp, setOtp] = useState('');
  const [countDown, setCountDown] = useState(0);
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const currentStep = CPC_BUY_STEPS.find((step) => step.key === stepParam) ?? CPC_BUY_STEPS[0];

  useEffect(() => {
    void dispatch(preorderPlanGet({vendor_id: vendorId}));
  }, [dispatch, vendorId]);

  const nextStep = () => {
    switch (stepParam) {
      case 'range-picker':
        if (filters.date && filters.day) {
          navigate(`/cpc/buy/${CPC_BUY_STEPS[1].key}?${query}`);
        } else {
          toast('تاریخ را انتخاب کنید');
        }
        break;
      case 'click-rate': {
        const clickCount = Number(filters.click);
        if (filters.click && filters.price) {
          if (clickCount >= preorderCPC.min_click && clickCount <= preorderCPC.max_click) {
            navigate(`/cpc/buy/${CPC_BUY_STEPS[2].key}?${query}`);
          } else {
            toast(
              `تعداد کلیک انتخابی باید بین بازه ${preorderCPC.min_click} و ${preorderCPC.max_click} باشد`
            );
          }
        } else {
          toast('تعداد کلیک و هزینه پیشنهادی را انتخاب کنید');
        }
        break;
      }
      default:
        break;
    }
  };

  const previousStep = () => {
    switch (stepParam) {
      case 'range-picker':
        navigate('/cpc/list');
        break;
      case 'click-rate':
        navigate(`/cpc/buy/${CPC_BUY_STEPS[0].key}?${query}`);
        break;
      case 'final-check':
        navigate(`/cpc/buy/${CPC_BUY_STEPS[1].key}?${query}`);
        break;
      default:
        break;
    }
  };

  const submitCpc = async () => {
    if (isCreatingCampaign) {
      return;
    }

    if (!filters.click || !filters.price || !filters.date) {
      toast('مقادیر کلیک و هزینه پیشنهادی و تاریخ را انتخاب کنید.');
      return;
    }

    setIsCreatingCampaign(true);

    try {
      await dispatch(
        preorderPlanCreate({
          vendor_id: vendorId,
          start_date: new Date(filters.date),
          cost_per_click: filters.price,
          demand_click_count: Number(filters.click)
        })
      ).unwrap();
      setCountDown(OTP_COUNTDOWN_SECONDS);
      setIsOtpOpen(true);
    } catch (error) {
      const requestError = error as {data?: {error?: string; errors?: {message?: string}}};
      toast(requestError.data?.errors?.message || requestError.data?.error || 'خطا');
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  const resendOtp = () => {
    setOtp('');
    void submitCpc();
  };

  const finalizePayment = async () => {
    if (otp.length !== 5) {
      toast('لطفا کد پیامک شده را وارد کنید');
      return;
    }

    try {
      const response = await dispatch(confirmCPCOTP({vendor_id: vendorId, code: otp})).unwrap();
      navigate('/cpc/payment-success', {state: {...response.data, items: response}});
    } catch {
      toast('کد اشتباه است');
    }
  };

  return (
    <PageLayout
      header={<BuyProgress steps={CPC_BUY_STEPS} currentStep={currentStep} />}
      footer={
        <div className="flex justify-between gap-4">
          <Button
            variant="link"
            color="secondary"
            className="bg-white px-0!"
            onClick={previousStep}
          >
            <u>مرحله قبل</u>
          </Button>
          <Button
            onClick={currentStep.key === 'final-check' ? submitCpc : nextStep}
            loading={currentStep.key === 'final-check' && isCreatingCampaign}
          >
            {currentStep.key === 'final-check' ? 'تایید و ایجاد کمپین' : 'مرحله بعد'}
          </Button>
        </div>
      }
    >
      {stepParam === 'range-picker' ? (
        <RangePickerStep filters={filters} setFilters={setFilters} />
      ) : null}
      {stepParam === 'click-rate' ? (
        <ClickRateStep filters={filters} setFilters={setFilters} />
      ) : null}
      {stepParam === 'final-check' ? (
        <FinalCheckStep
          filters={filters}
          steps={CPC_BUY_STEPS}
          query={query}
          payment={{
            vendorNumber: String(vendorNumber || ''),
            taxRate: preorderCPC.tax_rate ?? 0,
            maxPrice: preorderCPC.max_price,
            confirmPaymentLoading
          }}
          otpPayment={{
            isOpen: isOtpOpen,
            otp,
            setOtp,
            countDown,
            setCountDown,
            onClose: () => setIsOtpOpen(false),
            onResend: resendOtp,
            onFinalize: finalizePayment
          }}
        />
      ) : null}
    </PageLayout>
  );
};

export default CpcBuy;
